import { computed, ref } from 'vue';
import {
  commissionGroupComplete,
  commissionSheetComplete,
} from './commissionSheetSectionValidation';

export function useRealEstateCommissionSheetNavigation({
  onPreview,
  state,
  t,
}) {
  const activeGroupId = ref('corretores_vendedores');
  const groupEnabled = ref({});
  const attemptedGroups = ref(new Set());

  const groups = computed(() => [
    {
      id: 'corretores_vendedores',
      options: state.catalog.value.corretores || [],
    },
    {
      id: 'corretores_opcionistas',
      options: state.catalog.value.corretores || [],
    },
    { id: 'gerentes', options: state.catalog.value.gerentes || [] },
    {
      id: 'gerentes_gerais',
      options: state.catalog.value.gerentes_gerais || [],
      toggleLabel: t(
        'REAL_ESTATE_COMMISSION_INSTALLMENT.SHEET.HAS_GENERAL_MANAGER'
      ),
    },
    {
      id: 'diretores_gerais',
      options: state.catalog.value.diretores_gerais || [],
      toggleLabel: t('REAL_ESTATE_COMMISSION_INSTALLMENT.SHEET.HAS_DIRECTOR'),
    },
    {
      id: 'repasse_franquias',
      options: state.catalog.value.empresas || [],
      toggleLabel: t('REAL_ESTATE_COMMISSION_INSTALLMENT.SHEET.HAS_TRANSFER'),
    },
    {
      id: 'royalties',
      options: state.catalog.value.empresas || [],
      single: true,
    },
    {
      id: 'comunicacao',
      options: state.catalog.value.empresas || [],
      single: true,
    },
    { id: 'empresas', options: state.catalog.value.empresas || [] },
  ]);

  const activeGroupIndex = computed(() =>
    groups.value.findIndex(group => group.id === activeGroupId.value)
  );
  const activeGroup = computed(
    () => groups.value[activeGroupIndex.value] || groups.value[0]
  );
  const groupIsEnabled = group => {
    if (!group.toggleLabel) return true;
    if (Object.hasOwn(groupEnabled.value, group.id))
      return groupEnabled.value[group.id];

    return Boolean(state.form.value[group.id]?.length);
  };
  const activeGroupEnabled = computed(() => groupIsEnabled(activeGroup.value));
  const activeGroupQuantity = computed(
    () => state.form.value[activeGroup.value?.id]?.length || 0
  );
  const groupTitle = group => {
    const labels = {
      corretores_vendedores: () =>
        t('REAL_ESTATE_COMMISSION_INSTALLMENT.GROUPS.CORRETORES_VENDEDORES'),
      corretores_opcionistas: () =>
        t('REAL_ESTATE_COMMISSION_INSTALLMENT.GROUPS.CORRETORES_OPCIONISTAS'),
      gerentes: () => t('REAL_ESTATE_COMMISSION_INSTALLMENT.GROUPS.GERENTES'),
      gerentes_gerais: () =>
        t('REAL_ESTATE_COMMISSION_INSTALLMENT.GROUPS.GERENTES_GERAIS'),
      diretores_gerais: () =>
        t('REAL_ESTATE_COMMISSION_INSTALLMENT.GROUPS.DIRETORES_GERAIS'),
      repasse_franquias: () =>
        t('REAL_ESTATE_COMMISSION_INSTALLMENT.GROUPS.REPASSE_FRANQUIAS'),
      royalties: () => t('REAL_ESTATE_COMMISSION_INSTALLMENT.GROUPS.ROYALTIES'),
      comunicacao: () =>
        t('REAL_ESTATE_COMMISSION_INSTALLMENT.GROUPS.COMUNICACAO'),
      empresas: () => t('REAL_ESTATE_COMMISSION_INSTALLMENT.GROUPS.EMPRESAS'),
    };

    return (labels[group] || labels.empresas)();
  };
  const groupComplete = group =>
    commissionGroupComplete(
      group,
      state.form.value[group.id] || [],
      groupIsEnabled(group)
    );
  const sheetComplete = computed(() =>
    commissionSheetComplete(
      groups.value,
      state.form.value,
      Object.fromEntries(
        groups.value.map(group => [group.id, groupIsEnabled(group)])
      )
    )
  );
  const activeGroupError = computed(
    () =>
      attemptedGroups.value.has(activeGroup.value?.id) &&
      !groupComplete(activeGroup.value)
  );
  const menuItems = computed(() =>
    groups.value.map(group => ({
      id: group.id,
      label: groupTitle(group.id),
      complete: groupComplete(group),
    }))
  );
  const isLastGroup = computed(
    () => activeGroupIndex.value === groups.value.length - 1
  );

  const selectGroup = groupId => {
    activeGroupId.value = groupId;
  };

  const setGroupEnabled = enabled => {
    const group = activeGroup.value;
    groupEnabled.value = { ...groupEnabled.value, [group.id]: enabled };
    if (!enabled) state.setRowCount(group.id, 0);
  };

  const markIncomplete = groupIds => {
    attemptedGroups.value = new Set(groupIds);
  };

  const moveGroup = async offset => {
    const nextIndex = activeGroupIndex.value + offset;
    if (nextIndex < 0 || nextIndex >= groups.value.length) return false;
    if (!groupComplete(activeGroup.value)) {
      markIncomplete([...attemptedGroups.value, activeGroup.value.id]);
      return false;
    }
    if (state.canEdit.value && !(await state.save({ silent: true })))
      return false;

    activeGroupId.value = groups.value[nextIndex].id;
    return true;
  };

  const previewSheet = async () => {
    if (!sheetComplete.value) {
      markIncomplete(
        groups.value
          .filter(group => !groupComplete(group))
          .map(group => group.id)
      );
      return false;
    }
    if (state.canEdit.value && !(await state.save({ silent: true })))
      return false;
    onPreview();
    return true;
  };

  return {
    activeGroup,
    activeGroupEnabled,
    activeGroupError,
    activeGroupIndex,
    activeGroupQuantity,
    groups,
    isLastGroup,
    menuItems,
    moveGroup,
    previewSheet,
    selectGroup,
    setGroupEnabled,
    sheetComplete,
    groupTitle,
  };
}
